import axios from 'axios';
import axiosInstance from './axiosCreator';
import { store } from '../store/store';
import { capture } from '../utils/analytics';
import {
  JobStatus,
  JobStage,
  mappingJobReceived,
  mappingJobsReceived,
  mappingJobsSelectors,
  updateMappingJobStatus,
  mappingJobsFailed,
  MergeStrategy,
  MappingRelevance,
} from '../slices/mappingJobsSlice/mappingJobsSlice';
import { mergePolledMappingJobs } from '../slices/mappingJobsSlice/mergePolledMappingJobs';
import {
  AI_BUDGET_EXCEEDED_CODE,
  DAILY_DOC_LIMIT_GLOBAL_CODE,
  PAGE_LIMIT_EXCEEDED_CODE,
} from '../constants/limits';

export interface DailyUploadLimitPayload {
  error: string;
  // 'DAILY_UPLOAD_LIMIT' → Free-tier cap (upgrade UX).
  // 'DAILY_DOC_LIMIT_GLOBAL' → everyone-including-paid global cap (support UX).
  code:
    | 'DAILY_UPLOAD_LIMIT'
    | typeof DAILY_DOC_LIMIT_GLOBAL_CODE
    | typeof AI_BUDGET_EXCEEDED_CODE;
  daily_limit: number | null;
  uploads_today: number;
  uploads_remaining: number | null;
  reset_at: string | null;
  access_level: string;
}

// A synchronous rejection of an over-limit PDF at upload time (before any job
// is created). The message carries the specific page count + limit.
export interface UploadPageLimitPayload {
  error: string;
  code: typeof PAGE_LIMIT_EXCEEDED_CODE;
  num_pages: number;
  page_limit: number;
}

/**
 * True when an upload was blocked by the global (everyone-including-paid) daily
 * document cap rather than the Free-tier cap — callers show the support toast
 * instead of the upgrade modal.
 */
export const isGlobalDailyDocLimitError = (error: unknown): boolean =>
  error instanceof MappingJobStartLimitError &&
  (error.code === DAILY_DOC_LIMIT_GLOBAL_CODE ||
    error.code === AI_BUDGET_EXCEEDED_CODE);

/**
 * True when an upload was rejected up-front because the document exceeds the
 * per-document page cap. Callers show the error's specific message as a plain
 * toast.
 */
export const isUploadPageLimitError = (error: unknown): boolean =>
  error instanceof MappingJobStartLimitError &&
  error.code === PAGE_LIMIT_EXCEEDED_CODE;

export class MappingJobStartLimitError extends Error {
  status: number;
  code?: string;
  payload?: DailyUploadLimitPayload;

  constructor(
    message: string,
    status: number,
    code?: string,
    payload?: DailyUploadLimitPayload
  ) {
    super(message);
    this.name = 'MappingJobStartLimitError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export async function startMappingJob({
  fileKey,
  lectureId,
  mergeStrategy,
  fileName,
  examId,
  standalone,
  mappingRelevance,
  contentSetExamIds,
  parentId,
}: {
  fileKey: string;
  lectureId?: string;
  mergeStrategy?: MergeStrategy;
  fileName?: string;
  mappingRelevance?: MappingRelevance;
  // Mapping jobs are scoped to CUSTOM exam ownership. In preclinical mode,
  // pass the active custom exam id; backend falls back to PRE_CLINICAL only
  // when no active custom exam exists.
  examId?: string;
  // A standalone document not tied to any exam — homed in the user's private
  // personal library. Mutually exclusive with `examId`.
  standalone?: boolean;
  // "Content Set" the user picked in the add-document modal (ORA-376): the
  // canonical exam ids whose content this lecture maps against. Empty/omitted →
  // the backend falls back to the owner's default (primary/Preclinical).
  contentSetExamIds?: string[];
  // Destination folder for folder-UI exams. Null explicitly targets exam root.
  parentId?: string | null;
}) {
  try {
    const response = await axiosInstance.post('/ai/map-content-async', {
      fileKey,
      lectureId,
      mergeStrategy,
      fileName,
      examId,
      standalone,
      mappingRelevance,
      contentSetExamIds,
      parentId,
    });

    store.dispatch(mappingJobReceived(response.data.data));

    const resolvedMappingRelevance =
      response?.data?.data?.mappingRelevance ?? mappingRelevance ?? null;

    capture('lecture_mapping_job_started', {
      jobId: response?.data?.data?.id,
      examId,
      lectureId,
      mergeStrategy: mergeStrategy ?? null,
      mappingRelevance: resolvedMappingRelevance,
      strictnessSetting: resolvedMappingRelevance,
      hasFileName: Boolean(fileName),
    });

    return response.data.data;
  } catch (error) {
    const errorStatus = axios.isAxiosError(error)
      ? (error.response?.status ?? null)
      : null;

    const errorCode = axios.isAxiosError<DailyUploadLimitPayload>(error)
      ? (error.response?.data?.code ?? null)
      : null;

    const errorMessage = axios.isAxiosError<DailyUploadLimitPayload>(error)
      ? (error.response?.data?.error ?? error.message)
      : error instanceof Error
        ? error.message
        : 'unknown_error';

    capture('lecture_mapping_job_failed', {
      examId,
      lectureId,
      mergeStrategy: mergeStrategy ?? null,
      mappingRelevance: mappingRelevance ?? null,
      strictnessSetting: mappingRelevance ?? null,
      errorStatus,
      errorCode,
      errorMessage,
      hasFileName: Boolean(fileName),
    });

    if (
      axios.isAxiosError<DailyUploadLimitPayload | UploadPageLimitPayload>(
        error
      )
    ) {
      const payload = error.response?.data;
      if (
        error.response?.status === 429 &&
        (payload?.code === 'DAILY_UPLOAD_LIMIT' ||
          payload?.code === DAILY_DOC_LIMIT_GLOBAL_CODE ||
          payload?.code === AI_BUDGET_EXCEEDED_CODE)
      ) {
        throw new MappingJobStartLimitError(
          payload.error || 'Daily upload limit reached.',
          error.response.status,
          payload.code,
          payload as DailyUploadLimitPayload
        );
      }
      // Instant page-cap rejection for an over-limit PDF (ORA-395): surface the
      // server's specific reason as a plain message, distinct from daily limits.
      if (
        error.response?.status === 400 &&
        payload?.code === PAGE_LIMIT_EXCEEDED_CODE
      ) {
        throw new MappingJobStartLimitError(
          payload.error || 'This document exceeds the page limit.',
          error.response.status,
          payload.code
        );
      }
    }

    throw error;
  }
}

export async function pollMappingJobs(examId: string) {
  try {
    const response = await axiosInstance.get(`/ai/jobs?examId=${examId}`);
    const serverJobs = response.data.data;

    // Preserve a just-inserted pending job the server hasn't returned yet, so a
    // poll that races the upload's create doesn't erase it (and stop polling).
    const merged = mergePolledMappingJobs(
      mappingJobsSelectors.selectAll(store.getState()),
      serverJobs,
      examId,
      Date.now()
    );

    store.dispatch(mappingJobsReceived(merged));
    return serverJobs;
  } catch (error) {
    const message =
      // @ts-expect-error - ignore type error
      error?.response?.data?.error || error?.message || 'Polling failed';

    store.dispatch(mappingJobsFailed(message));
    return null;
  }
}

export const updateMappingJob = async (
  jobId: string,
  {
    status,
    stage,
    lectureId,
  }: {
    status?: JobStatus;
    stage?: JobStage;
    lectureId?: string;
  }
) => {
  try {
    if (status !== undefined) {
      store.dispatch(updateMappingJobStatus({ jobId, status }));
    }
    if (stage !== undefined) {
      store.dispatch(updateMappingJobStatus({ jobId, stage }));
    }

    const requestBody: {
      status?: JobStatus;
      stage?: JobStage;
      lectureId?: string;
    } = {};

    if (status !== undefined) {
      requestBody.status = status;
    }
    if (stage !== undefined) {
      requestBody.stage = stage;
    }
    if (lectureId !== undefined) {
      requestBody.lectureId = lectureId;
    }

    const response = await axiosInstance.put(`/ai/job/${jobId}`, requestBody);
    store.dispatch(mappingJobReceived(response.data.data));
    return response.data.data;
  } catch (error) {
    console.error('Error updating mapping job:', error);
    throw error;
  }
};

export const bulkRerunMappingJobs = async (examId: string) => {
  const response = await axiosInstance.post(`/ai/jobs/rerun-lectures`, {
    examId,
  });

  return response.data.data;
};

export const fetchDailyUploadCount = async (): Promise<{
  uploadsToday: number;
  // The user's effective per-document page cap (ORA-761). Optional: older
  // backends do not send it.
  documentPageLimit?: number;
}> => {
  const response = await axiosInstance.get('/ai/uploads/today');
  return response.data.data;
};

export type FacultyStudentPerformanceSummary = {
  strengths: string;
  weaknesses: string;
  performanceTrend: string;
};

export const fetchFacultyStudentPerformanceSummary = async ({
  cohortId,
  rosterEntryId,
}: {
  cohortId: string;
  rosterEntryId: string;
}): Promise<FacultyStudentPerformanceSummary> => {
  const response = await axiosInstance.get<{
    success: boolean;
    data: FacultyStudentPerformanceSummary;
  }>(
    `/ai/institution-admin/cohorts/${cohortId}/members/${rosterEntryId}/performance-summary`
  );

  return response.data.data;
};
